import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import type * as Scope from "effect/Scope";

import type { ConnectionCatalogEntry } from "./catalog.ts";
import type {
  ConnectionAttemptError,
  ConnectionAttemptStage,
  PreparedConnection,
} from "./model.ts";
import * as RpcSession from "../rpc/session.ts";

export interface EnvironmentConnectionLease {
  readonly prepared: PreparedConnection;
  readonly session: RpcSession.RpcSession;
}

export class ConnectionDriver extends Context.Service<
  ConnectionDriver,
  {
    readonly connect: (
      entry: ConnectionCatalogEntry,
      reportStage: (stage: ConnectionAttemptStage) => Effect.Effect<void>,
    ) => Effect.Effect<EnvironmentConnectionLease, ConnectionAttemptError, Scope.Scope>;
  }
>()("@heydex/client-runtime/connection/driver/ConnectionDriver") {}

export function prepareConnection(entry: ConnectionCatalogEntry): PreparedConnection {
  const target = entry.target;
  return {
    environmentId: target.environmentId,
    label: target.label,
    httpBaseUrl: target.httpBaseUrl,
    target,
  };
}

/** @public Service construction is part of the canonical Effect module API. */
export const make = Effect.gen(function* () {
  const sessions = yield* RpcSession.RpcSessionFactory;

  const connect = Effect.fn("ConnectionDriver.connect")(function* (
    entry: ConnectionCatalogEntry,
    reportStage: (stage: ConnectionAttemptStage) => Effect.Effect<void>,
  ) {
    yield* Effect.annotateCurrentSpan({ "connection.environment.id": entry.target.environmentId });
    const prepared = prepareConnection(entry);
    yield* reportStage("opening");
    const session = yield* sessions.connect(prepared);
    yield* reportStage("synchronizing");
    yield* session.ready;
    return { prepared, session } satisfies EnvironmentConnectionLease;
  });

  return ConnectionDriver.of({ connect });
});

export const layer = Layer.effect(ConnectionDriver, make);
